'use client'

import { cn } from '@/lib/utils'
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'

/**
 * Typographic option wheel, adapted from React Bits (reactbits.dev/components/option-wheel).
 * Options sit on an arc anchored to one edge; the centered option is lit and the
 * rest fade and blur with distance. Scroll, drag, arrow keys or click to spin.
 */

type Side = 'left' | 'right'

export type OptionWheelItem = string | { label: string; note?: string }

export interface OptionWheelProps {
  items: OptionWheelItem[]
  defaultSelected?: number
  onChange?: (index: number, label: string) => void
  onConfirm?: () => void
  onSelect?: (index: number) => void
  renderItem?: (index: number, selected: boolean) => ReactNode
  itemClassName?: string
  ariaLabel?: string
  ariaDescribedBy?: string
  /** Resting color of the labels (any CSS color, incl. var()). */
  textColor?: string
  /** Color the centered label blends toward. */
  activeColor?: string
  side?: Side
  /** Label font size in rem. */
  fontSize?: number
  /** Row height as a multiple of the font size. */
  spacing?: number
  curve?: number
  /** Degrees between neighbouring options; higher curls the arc tighter. */
  tilt?: number
  rotateItems?: boolean
  /** Blur in px added per step from the middle. */
  blur?: number
  /** Opacity lost per step from the middle. */
  fade?: number
  minOpacity?: number
  /** Easing time constant in ms; higher feels heavier. */
  smoothing?: number
  /** Px between the anchored edge and the centered option. */
  inset?: number
  loop?: boolean
  draggable?: boolean
  /** Fix the wheel height to this many rows; otherwise it fills its parent. */
  visibleItems?: number
  /** Vertical anchor of the active option, 0 (top) to 1 (bottom). */
  align?: number
  className?: string
}

type WheelConfig = {
  count: number
  labels: string[]
  rowH: number
  curve: number
  tilt: number
  rotateItems: boolean
  blur: number
  fade: number
  minOpacity: number
  side: Side
  loop: boolean
  smoothing: number
  draggable: boolean
}

const itemLabel = (item: OptionWheelItem) => (typeof item === 'string' ? item : item.label)
const itemNote = (item: OptionWheelItem) => (typeof item === 'string' ? undefined : item.note)

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

/** Where an option sits on the arc, given its signed distance from the current position. */
function layoutItem(d: number, cfg: WheelConfig) {
  const dist = Math.abs(d)
  const mirror = cfg.side === 'right' ? -1 : 1
  // Options sit on a circle whose radius keeps the arc length between two
  // neighbours equal to one row height, so tilt controls how tightly it curls.
  const tiltRad = (cfg.tilt * Math.PI) / 180
  const R = tiltRad > 0.0005 ? cfg.rowH / tiltRad : 0
  let x = 0
  let y = d * cfg.rowH
  let rot = 0
  if (R > 0) {
    const ang = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, d * tiltRad))
    y = R * Math.sin(ang)
    x = -mirror * R * (1 - Math.cos(ang)) * cfg.curve
    rot = cfg.rotateItems ? (mirror * ang * 180) / Math.PI : 0
  }
  return {
    transform: `translate(${x.toFixed(2)}px, calc(${y.toFixed(2)}px - 50%)) rotate(${rot.toFixed(3)}deg)`,
    opacity: String(Math.max(cfg.minOpacity, 1 - dist * cfg.fade)),
    filter: cfg.blur > 0 ? `blur(${(dist * cfg.blur).toFixed(2)}px)` : 'none',
    progress: Math.max(0, 1 - Math.min(dist, 1)).toFixed(4),
  }
}

function signedDistance(index: number, position: number, cfg: WheelConfig) {
  let d = index - position
  if (cfg.loop && cfg.count > 1) {
    d = ((d % cfg.count) + cfg.count) % cfg.count
    if (d > cfg.count / 2) d -= cfg.count
  }
  return d
}

export function OptionWheel({
  items,
  defaultSelected = 0,
  onChange,
  onConfirm,
  onSelect,
  renderItem,
  itemClassName,
  ariaLabel = 'Options',
  ariaDescribedBy,
  textColor = 'var(--muted-foreground)',
  activeColor = 'var(--foreground)',
  side = 'left',
  fontSize = 3,
  spacing = 1.4,
  curve = 1,
  tilt = 6,
  rotateItems = true,
  blur = 2,
  fade = 0.25,
  minOpacity = 0.05,
  smoothing = 200,
  inset = 80,
  loop = false,
  draggable = true,
  visibleItems,
  align = 0.5,
  className,
}: OptionWheelProps) {
  const idPrefix = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  const posRef = useRef(defaultSelected)
  const targetRef = useRef(defaultSelected)
  const rafRef = useRef<number | null>(null)
  const lastRef = useRef(0)
  const onChangeRef = useRef(onChange)
  const selectedRef = useRef(defaultSelected)
  const wheelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragRef = useRef<{ y: number; start: number; id: number } | null>(null)
  const dragMovedRef = useRef(false)
  const [selectedIndex, setSelectedIndex] = useState(defaultSelected)
  const [isDragging, setIsDragging] = useState(false)
  const [remPx, setRemPx] = useState(16)

  useEffect(() => {
    setRemPx(parseFloat(getComputedStyle(document.documentElement).fontSize) || 16)
  }, [])

  const labels = items.map(itemLabel)
  const rowH = Math.max(fontSize * spacing * remPx, 1)
  const cfg: WheelConfig = {
    count: items.length,
    labels,
    rowH,
    curve,
    tilt,
    rotateItems,
    blur,
    fade,
    minOpacity,
    side,
    loop,
    smoothing,
    draggable,
  }
  const cfgRef = useRef(cfg)

  useIsomorphicLayoutEffect(() => {
    onChangeRef.current = onChange
    cfgRef.current = cfg
  })

  const paint = useCallback((position: number) => {
    const current = cfgRef.current
    itemRefs.current.forEach((el, index) => {
      if (!el) return
      const { transform, opacity, filter, progress } = layoutItem(
        signedDistance(index, position, current),
        current,
      )
      el.style.transform = transform
      el.style.opacity = opacity
      el.style.filter = filter
      el.style.setProperty('--ow-p', progress)
    })
  }, [])

  // Single rAF loop that eases the wheel toward its target with frame-rate
  // independent exponential smoothing, repainting every option along the arc.
  const runFrame = useCallback(
    (now: number) => {
      const dt = Math.min((now - lastRef.current) / 1000, 0.05)
      lastRef.current = now
      const tau = Math.max(cfgRef.current.smoothing, 1) / 1000
      const k = 1 - Math.exp(-dt / tau)

      const target = targetRef.current
      let next = posRef.current + (target - posRef.current) * k
      const settled = Math.abs(target - next) < 0.001
      if (settled) next = target
      posRef.current = next
      paint(next)

      rafRef.current = settled ? null : requestAnimationFrame(runFrame)
    },
    [paint],
  )

  const startLoop = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    lastRef.current = performance.now()
    rafRef.current = requestAnimationFrame(runFrame)
  }, [runFrame])

  const applyTarget = useCallback(
    (value: number, snap: boolean) => {
      const current = cfgRef.current
      let v = value
      if (!current.loop) v = Math.min(Math.max(v, 0), Math.max(current.count - 1, 0))
      if (snap) v = Math.round(v)
      targetRef.current = v
      const idx = ((Math.round(v) % current.count) + current.count) % current.count
      if (idx !== selectedRef.current) {
        selectedRef.current = idx
        setSelectedIndex(idx)
        onChangeRef.current?.(idx, current.labels[idx])
      }
      startLoop()
    },
    [startLoop],
  )

  // Wheel / touchpad scrolling, registered manually so it can be non-passive.
  // At either end of a non-looping wheel the event is released to the page
  // (and Lenis) so the wheel never traps page scroll.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.deltaY === 0) return
      const current = cfgRef.current
      const delta = e.deltaMode === 1 ? e.deltaY * 24 : e.deltaY
      const atStart = targetRef.current <= 0 && delta < 0
      const atEnd = targetRef.current >= current.count - 1 && delta > 0
      if (!current.loop && (atStart || atEnd)) return

      e.preventDefault()
      ;(e as WheelEvent & { lenisStopPropagation?: boolean }).lenisStopPropagation = true
      // Cap each event at one step so notchy mouse wheels move one option per
      // click while touchpads still scroll continuously.
      const step = Math.max(-1, Math.min(1, delta / current.rowH))
      applyTarget(targetRef.current + step, false)
      if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current)
      wheelTimerRef.current = setTimeout(() => applyTarget(targetRef.current, true), 140)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      el.removeEventListener('wheel', onWheel)
      if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current)
    }
  }, [applyTarget])

  const handlePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!cfgRef.current.draggable || !e.isPrimary || e.button !== 0) return
    rootRef.current?.focus({ preventScroll: true })
    dragRef.current = { y: e.clientY, start: targetRef.current, id: e.pointerId }
    dragMovedRef.current = false
    setIsDragging(true)
  }, [])

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      if (!drag) return
      const dy = e.clientY - drag.y
      if (!dragMovedRef.current && Math.abs(dy) > 4) {
        dragMovedRef.current = true
        // Capture only once a real drag starts so plain clicks still reach items.
        rootRef.current?.setPointerCapture(drag.id)
      }
      if (dragMovedRef.current) applyTarget(drag.start - dy / cfgRef.current.rowH, false)
    },
    [applyTarget],
  )

  const handlePointerEnd = useCallback(() => {
    if (!dragRef.current) return
    dragRef.current = null
    setIsDragging(false)
    if (dragMovedRef.current) applyTarget(targetRef.current, true)
  }, [applyTarget])

  const handleItemClick = useCallback(
    (index: number) => {
      if (dragMovedRef.current) return
      const current = cfgRef.current
      const cur = targetRef.current
      let d = index - (((cur % current.count) + current.count) % current.count)
      if (current.loop && current.count > 1) {
        if (d > current.count / 2) d -= current.count
        else if (d < -current.count / 2) d += current.count
      }
      applyTarget(cur + d, true)
      onSelect?.(index)
    },
    [applyTarget, onSelect],
  )

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && onConfirm) {
        e.preventDefault()
        onConfirm()
        return
      }
      let next: number | null = null
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = Math.round(targetRef.current) - 1
      else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = Math.round(targetRef.current) + 1
      else if (e.key === 'Home') next = 0
      else if (e.key === 'End') next = cfgRef.current.count - 1
      if (next == null) return
      e.preventDefault()
      applyTarget(next, true)
    },
    [applyTarget, onConfirm],
  )

  // Paint synchronously before first paint (no stacked-items flash), then
  // re-settle whenever geometry props change.
  useIsomorphicLayoutEffect(() => {
    paint(posRef.current)
  }, [paint])

  useEffect(() => {
    applyTarget(targetRef.current, false)
  }, [items.length, rowH, curve, tilt, rotateItems, blur, fade, minOpacity, side, loop, smoothing, applyTarget])

  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    },
    [],
  )

  const optionId = (index: number) => `${idPrefix}-option-${index}`

  return (
    <div
      ref={rootRef}
      role="listbox"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-activedescendant={optionId(selectedIndex)}
      className={cn(
        'relative w-full select-none overflow-hidden rounded-2xl outline-none [touch-action:pan-y] focus-visible:ring-2 focus-visible:ring-brand/40',
        visibleItems == null && 'h-full',
        isDragging ? 'cursor-grabbing' : 'cursor-grab',
        className,
      )}
      style={
        {
          '--ow-text-color': textColor,
          '--ow-active-color': activeColor,
          '--ow-font-size': `${fontSize}rem`,
          '--ow-inset': `${inset}px`,
          '--ow-align': `${align * 100}%`,
          height: visibleItems != null ? `${visibleItems * fontSize * spacing}rem` : undefined,
        } as CSSProperties
      }
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onLostPointerCapture={(event) => {
        // Touch starts with implicit capture on the label. Transferring that
        // capture to the wheel must not end the drag when the label loses it.
        if (event.target === event.currentTarget) handlePointerEnd()
      }}
      onKeyDown={handleKeyDown}
    >
      {items.map((item, index) => {
        const note = itemNote(item)
        return (
          <div
            key={`${itemLabel(item)}-${index}`}
            id={optionId(index)}
            ref={(el) => {
              itemRefs.current[index] = el
            }}
            role="option"
            aria-label={renderItem ? itemLabel(item) : undefined}
            aria-selected={selectedIndex === index}
            onClick={() => handleItemClick(index)}
            className={cn(
              'absolute top-[var(--ow-align)] flex cursor-pointer items-start gap-[0.25em] whitespace-nowrap font-sans leading-none tracking-[-0.045em] opacity-0 will-change-[transform,opacity,filter] [font-size:var(--ow-font-size)] [color:color-mix(in_srgb,var(--ow-active-color)_calc(var(--ow-p,0)*100%),var(--ow-text-color))]',
              side === 'right'
                ? 'right-[var(--ow-inset)] origin-right'
                : 'left-[var(--ow-inset)] origin-left',
              selectedIndex === index ? 'font-semibold' : 'font-light',
              itemClassName,
            )}
          >
            {renderItem ? renderItem(index, selectedIndex === index) : itemLabel(item)}
            {!renderItem && note && (
              <span className="mt-[0.2em] text-[0.26em] font-bold uppercase tracking-[0.16em] text-brand">
                {note}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}
